import React, { useState, useEffect, useContext } from "react";
import { Button } from "@/components/ui/button";
import { Menu, Phone, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DemoPopupContext } from "@/pages/LandingPage";
import { useSettings } from "@/hooks/useSettings";
import { useNavigate } from "react-router-dom";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [hideOnMobile, setHideOnMobile] = useState(false);
  const demoPopupCtx = useContext(DemoPopupContext);
  const { settings } = useSettings();
  const navigate = useNavigate();

  const handleCTA = () => {
    if (demoPopupCtx) {
      demoPopupCtx.openDemoPopup();
    } else {
      navigate("/?demo=1");
    }
  };

  useEffect(() => {
    let lastY = 0;
    const handleScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 20);
      // On mobile (<768px): hide on scroll down, show on scroll up
      if (window.innerWidth < 768) {
        setHideOnMobile(y > 60 && y > lastY);
      } else {
        setHideOnMobile(false);
      }
      lastY = y;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const navLinks = [
    { label: "Solutions", id: "solution" },
    { label: "Packs", id: "use-cases" },
    { label: "Résultats", id: "results" },
    { label: "Calculateur", id: "calculator" },
    { label: "FAQ", id: "faq" },
  ];

  const logoUrl = settings?.logo_light || "/logo_transparent.png";

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        hideOnMobile ? "-translate-y-full" : "translate-y-0"
      } ${
        isScrolled
          ? "bg-white/95 backdrop-blur-sm border-b-2 border-slate-200"
          : "bg-transparent"
      }`}
      style={isScrolled && !hideOnMobile ? { boxShadow: '0 2px 0px rgba(30, 90, 158, 0.08)' } : {}}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button
              onClick={() => scrollToSection("hero")}
              className="flex items-center hover:opacity-80 transition-opacity"
              data-testid="navbar-logo"
            >
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt="Lead Artisan" 
                  className="h-14 lg:h-16 w-auto"
                />
              ) : (
                <span className="text-lg sm:text-xl lg:text-2xl font-black text-slate-900" style={{ fontFamily: 'Barlow, sans-serif' }}>
                  <span className="text-primary">Lead</span>Artisan
                </span>
              )}
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                data-testid={`nav-link-${link.id}`}
                className="px-3 lg:px-4 py-2 text-sm lg:text-base font-semibold text-slate-600 hover:text-primary transition-colors rounded-md whitespace-nowrap"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* CTA Button Desktop */}
          <div className="hidden md:block">
            <Button
              onClick={handleCTA}
              data-testid="navbar-cta"
              className="bg-accent hover:bg-orange-600 text-white shadow-lg font-bold transition-all hover:scale-105 text-sm lg:text-base px-5 lg:px-6 rounded-md"
            >
              <Phone className="mr-2 w-4 h-4" />
              Démo gratuite
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-slate-900" data-testid="navbar-mobile-menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[320px] bg-white border-l-2 border-slate-200">
                <div className="flex flex-col space-y-2 mt-8">
                  {/* Logo in mobile menu */}
                  {logoUrl && (
                    <div className="mb-4 pb-4 border-b-2 border-slate-200">
                      <img 
                        src={logoUrl} 
                        alt="Lead Artisan" 
                        className="h-10 w-auto"
                      />
                    </div>
                  )}
                  {navLinks.map((link) => (
                    <button
                      key={link.id}
                      onClick={() => scrollToSection(link.id)}
                      data-testid={`mobile-nav-${link.id}`}
                      className="text-left px-4 py-3 text-base font-semibold text-slate-700 hover:text-primary hover:bg-slate-50 rounded-md transition-colors border border-transparent hover:border-slate-200"
                    >
                      {link.label}
                    </button>
                  ))}
                  <div className="pt-4">
                    <Button
                      onClick={handleCTA}
                      data-testid="mobile-cta"
                      className="bg-accent hover:bg-orange-600 text-white shadow-lg font-bold w-full rounded-md"
                    >
                      <Phone className="mr-2 w-4 h-4" />
                      Démo gratuite
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
